import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AfterViewInit, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CORE_MOMENT_DATA_SERVICE, MomentDataService } from '@app/app/page-modules/folio/shared-moment-module/services/data.service';
import { COMMENT_DATA_SERVICE, CommentDataService } from '@app/app/shared/services/comment/comment-data.service';
import { LucideMessageSquare } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subject, debounceTime, finalize, takeUntil } from 'rxjs';
import { UserDetailsSummary } from '../../../user-auth/models';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';
import { Comment, CommentSortOption, UiComment } from '../../models/comment/comment.model';

@Component({
  selector: 'ptl-v3-moment-card-comments',
  templateUrl: './v3-moment-card-comments.component.html',
  styleUrls: ['./v3-moment-card-comments.component.scss'],
})
export class V3MomentCardCommentsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() momentId!: string;
  @Input() initialCommentsCount = 0;
  @Output() commentCountUpdate = new EventEmitter<number>();

  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  @Select(UserAuthState.userDetailsData)
  userDetails$: Observable<UserDetailsSummary>;

  protected readonly LucideMessageSquare = LucideMessageSquare;

  comments: UiComment[] = [];
  totalCommentsCount = 0;
  isLoading = false;
  hasMore = false;
  currentPage = 0;
  currentSort: CommentSortOption['value'] = 'NEWEST';
  currentUserId: string | null = null;

  readonly sortOptions: CommentSortOption[] = [
    { value: 'NEWEST', label: this.translocoService.translate('translations.comments.sort.newest') },
    { value: 'OLDEST', label: this.translocoService.translate('translations.comments.sort.oldest') },
    { value: 'MOST_LIKED', label: this.translocoService.translate('translations.comments.sort.mostLiked') },
    { value: 'MOST_REPLIED', label: this.translocoService.translate('translations.comments.sort.mostReplied') },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    public translocoService: TranslocoService,
    @Inject(COMMENT_DATA_SERVICE) private commentService: CommentDataService,
    @Inject(CORE_MOMENT_DATA_SERVICE) private momentService: MomentDataService,
  ) {}

  ngOnInit(): void {
    this.totalCommentsCount = this.initialCommentsCount;

    this.userDetails$.pipe(takeUntil(this.destroy$)).subscribe((userDetails) => {
      if (userDetails) {
        this.currentUserId = userDetails.uid;
      }
    });

    this.loadComments();
  }

  ngAfterViewInit(): void {
    if (this.viewport) {
      this.viewport.scrolledIndexChange.pipe(takeUntil(this.destroy$), debounceTime(300)).subscribe(() => {
        this.onScroll();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadComments(reset: boolean = false): void {
    if (this.isLoading) return;

    this.isLoading = true;

    if (reset) {
      this.currentPage = 0;
      this.comments = [];
    }

    this.commentService
      .getComments(this.momentId, this.currentPage, 15, this.currentSort)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (result) => {
          if (result.isSuccess) {
            const newComments = this.toUiComments(result.value.content);

            if (reset) {
              this.comments = newComments;
            } else {
              this.comments = [...this.comments, ...newComments];
            }

            this.hasMore = result.value.currentPageNumber < result.value.numberOfPages;
            this.currentPage++;
          } else {
            console.error('Error loading comments:', result.error);
          }
        },
        error: (error) => {
          console.error('Error loading comments:', error);
        },
      });
  }

  onScroll(): void {
    if (!this.viewport || this.isLoading || !this.hasMore) return;

    const end = this.viewport.getRenderedRange().end;
    const total = this.viewport.getDataLength();

    if (end >= total - 2) {
      this.loadComments();
    }
  }

  onSortChange(sort: CommentSortOption['value']): void {
    if (this.currentSort === sort) return;

    this.currentSort = sort;
    this.loadComments(true);
  }

  onCommentCreated(comment: Comment): void {
    const uiComment = this.toUiComment(comment);
    this.comments = [uiComment, ...this.comments];
    this.refreshMomentDetails();
  }

  onCommentUpdated(updatedComment: Comment): void {
    this.comments = this.comments.map((comment) => {
      if (comment.id === updatedComment.id) {
        return { ...comment, content: updatedComment.content, updatedAt: updatedComment.updatedAt };
      }

      const flatReplies = comment.flatReplies?.map((reply) =>
        reply.id === updatedComment.id ? { ...reply, content: updatedComment.content, updatedAt: updatedComment.updatedAt } : reply,
      );

      return flatReplies ? { ...comment, flatReplies } : comment;
    });
  }

  onCommentDeleted(commentId: string): void {
    this.comments = this.comments
      .filter((comment) => comment.id !== commentId)
      .map((comment) => {
        if (!comment.flatReplies?.some((reply) => reply.id === commentId)) {
          return comment;
        }

        const removedIds = this.collectReplyAndDescendantIds(comment.flatReplies, commentId);
        const flatReplies = comment.flatReplies.filter((reply) => !removedIds.has(reply.id));

        return {
          ...comment,
          flatReplies,
          repliesCount: flatReplies.filter((reply) => reply.parentCommentId === comment.id).length,
          totalRepliesCount: (comment.totalRepliesCount ?? comment.repliesCount) - removedIds.size,
        };
      });

    this.refreshMomentDetails();
  }

  private collectReplyAndDescendantIds(flatReplies: UiComment[], rootId: string): Set<string> {
    const removedIds = new Set<string>([rootId]);

    let foundNewDescendant = true;
    while (foundNewDescendant) {
      foundNewDescendant = false;
      for (const reply of flatReplies) {
        if (!removedIds.has(reply.id) && removedIds.has(reply.parentCommentId)) {
          removedIds.add(reply.id);
          foundNewDescendant = true;
        }
      }
    }

    return removedIds;
  }

  onCommentLiked(commentId: string, isLiked: boolean): void {
    const comment = this.findComment(commentId);
    if (comment) {
      comment.likes.liked = isLiked;
      comment.likes.totalLikes += isLiked ? 1 : -1;
    }
  }

  private findComment(commentId: string): UiComment | undefined {
    for (const comment of this.comments) {
      if (comment.id === commentId) {
        return comment;
      }

      const reply = comment.flatReplies?.find((r) => r.id === commentId);
      if (reply) {
        return reply;
      }
    }
    return undefined;
  }

  onReplyCreated(reply: Comment, parentCommentId: string, replyingToAuthorName?: string): void {
    const uiReply = this.toUiComment(reply, replyingToAuthorName);

    this.comments = this.comments.map((comment) => {
      const isDirectReplyToThisComment = comment.id === parentCommentId;
      const isReplyToOneOfThisThreadsFlattenedReplies = !!comment.flatReplies?.some((r) => r.id === parentCommentId);

      if (!isDirectReplyToThisComment && !isReplyToOneOfThisThreadsFlattenedReplies) {
        return comment;
      }

      return {
        ...comment,
        flatReplies: [...(comment.flatReplies ?? []), uiReply],
        repliesCount: isDirectReplyToThisComment ? comment.repliesCount + 1 : comment.repliesCount,
        totalRepliesCount: (comment.totalRepliesCount ?? comment.repliesCount) + 1,
        isExpanded: true,
      };
    });

    this.refreshMomentDetails();
  }

  toUiComment(comment: Comment, replyingToAuthorName?: string): UiComment {
    return {
      ...comment,
      isExpanded: false,
      isEditing: false,
      isReplying: false,
      flatReplies: undefined,
      replyingToAuthorName,
    };
  }

  toUiComments(comments: Comment[]): UiComment[] {
    return comments.map((comment) => this.toUiComment(comment));
  }

  private refreshMomentDetails(): void {
    this.momentService.getMomentDetails(this.momentId).subscribe({
      next: (result) => {
        if (result.isSuccess && result.value) {
          this.totalCommentsCount = result.value.commentsCount || 0;
          this.commentCountUpdate.emit(this.totalCommentsCount);
        }
      },
      error: (error) => {
        console.error('Error refreshing moment details:', error);
      },
    });
  }
}
